import React, { useCallback, useEffect, useState } from "react";
import { FontIcon, mergeStyleSets, MessageBarType, PrimaryButton, Text, useTheme } from "@fluentui/react";
import formatTimeDigital from "../helpers/timeHelpers";
import useInterval from "../hooks/useInterval";
import caseNestService from "../services/caseNestService";
import ActivityDescriptionDialog from "../dialogs/ActivityDescriptionDialog";
import ResetTimeDialog from "../dialogs/ResetTimeDialog";

const timerState = {
  PLAYING: "PLAYING",
};

const getClassNames = (theme, isPlaying) =>
  mergeStyleSets({
    root: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "space-between",
      flexGrow: 1,
      padding: theme.spacing.s1,
      height: "100%",
    },
    timer: {
      width: "100%",
      display: "flex",
      justifyContent: "center",
    },
    buttonContainer: {
      display: "flex",
      justifyContent: "flex-end",
    },
    saveButtonContainer: {
      width: "100%",
      marginTop: theme.spacing.l2,
    },
    saveButton: {
      width: "100%",
    },
    timerContainer: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      flexGrow: 1,
      margin: theme.spacing.s2,
      padding: theme.spacing.l2,
      border: `1px solid ${theme.palette.borderLight}`,
      height: "100%",
      width: "75%",
      maxWidth: 300,
      borderRadius: 3,
      background: !isPlaying ? theme.palette.white : theme.palette.timerRed,
    },
    timerTitle: {
      fontWeight: 400,
      color: !isPlaying ? theme.palette.themePrimary : theme.palette.white,
      margin: theme.spacing.m,
    },
    timerClock: {
      fontWeight: 400,
      color: !isPlaying ? theme.palette.themePrimary : theme.palette.white,
      margin: theme.spacing.m,
    },
    actionButton: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      margin: theme.spacing.m,
      cursor: "pointer",
      border: `2px solid ${!isPlaying ? theme.palette.themePrimary : theme.palette.white}`,
      borderRadius: "50%",
      minWidth: 140,
      minHeight: 140,
      background: !isPlaying ? theme.palette.white : "#db5845",
      color: !isPlaying ? theme.palette.themePrimary : theme.palette.white,
      selectors: {
        "&:hover": {
          backgroundColor: theme.palette.buttonHover,
        },
      },
    },
    resetBtn: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      margin: theme.spacing.m,
    },
    actionIcon: {
      fontSize: 40,
      marginBottom: theme.spacing.s1,
    },
    actionText: {
      color: !isPlaying ? theme.palette.themePrimary : theme.palette.white,
    },
    resetIcon: {
      color: theme.palette.white,
      fontSize: 14,
      marginRight: theme.spacing.s1,
    },
  });

const UserTimer = ({
  start,
  stop,
  reset,
  getTimerState,
  getTime,
  setTime,
  liveCaseDocumentId,
  displayTimer,
  caseId,
  setMessageBar,
  onTimerPause,
}) => {
  const theme = useTheme();
  let {
    root,
    saveButtonContainer,
    saveButton,
    timerContainer,
    timerTitle,
    timerClock,
    actionButton,
    resetBtn,
    actionIcon,
    actionText,
    resetIcon,
  } = getClassNames(theme, getTime() !== 0 && getTimerState() !== timerState.PLAYING);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isResetDialogOpen, setIsResetDialogOpen] = useState(false);
  const [activityDescription, setActivityDescription] = useState("");
  const [saving, setSaving] = useState(false);

  const handleStart = () => {
    start();
  };

  const handleStop = async () => {
    cancelInterval();
    stop();
    fetchWordTimeEvents();
  };

  const handleReset = () => {
    reset();
    stop();
    setTime(0);
  };

  const handleManualReset = async () => {
    setIsResetDialogOpen(false);
    try {
      await caseNestService.deleteWordTimeEvents(liveCaseDocumentId);
      handleReset();
    } catch (e) {
      console.error(e);
      setMessageBar({ isOpen: true, message: e?.message || "Failed to reset timer", type: MessageBarType.error });
    }
  };

  const handleCloseDialog = () => {
    setActivityDescription("");
    setDialogOpen(false);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await caseNestService.addActivity({
        liveCaseDocumentId,
        caseId,
        description: activityDescription,
      });
      handleReset();
      handleCloseDialog();
      setMessageBar({ isOpen: true, message: "Drafting activity saved!", type: MessageBarType.success });
    } catch (e) {
      console.error(e);
      setMessageBar({ isOpen: true, message: e?.message || "Failed to save activity", type: MessageBarType.error });
    } finally {
      setSaving(false);
    }
  };

  const [cancelInterval] = useInterval({
    callback: async () => {
      try {
        const response = await caseNestService.addWordTimeEvent({
          liveCaseDocumentId,
        });
        const { pauseTimer, documentDuration } = response.data;
        if (pauseTimer) {
          cancelInterval();
          stop();
          onTimerPause();
          setTime(documentDuration * 1000);
        }
      } catch (e) {
        console.error(e);
      }
    },
    delay: 10000,
    isActive: getTimerState() === timerState.PLAYING && !!liveCaseDocumentId,
  });

  const fetchWordTimeEvents = useCallback(async () => {
    if (!liveCaseDocumentId) return;
    try {
      const response = await caseNestService.fetchWordTimeEvents({ liveCaseDocumentId });
      const duration = response.data;
      if (duration != null) setTime(duration * 1000);
    } catch (e) {
      console.error(e);
    }
  }, [liveCaseDocumentId]);

  useEffect(() => {
    fetchWordTimeEvents();
  }, [fetchWordTimeEvents]);

  if (!displayTimer) return null;

  return (
    <div className={root}>
      <div className={timerContainer}>
        <Text className={timerTitle} variant="xLarge">
          {getTime() !== 0 && getTimerState() !== timerState.PLAYING ? "Timer Paused" : "Internal Timer"}
        </Text>
        <Text className={timerClock} variant="xxLargePlus">
          {formatTimeDigital(getTime() / 1000)}
        </Text>
        {getTime() === 0 && getTimerState() !== timerState.PLAYING && (
          <div className={actionButton} aria-label="Play button" onClick={handleStart}>
            <FontIcon className={actionIcon} iconName="Play" />
            <Text className={actionText} variant="medium">
              Start
            </Text>
          </div>
        )}
        {getTimerState() === timerState.PLAYING && (
          <div className={actionButton} aria-label="Pause button" onClick={handleStop}>
            <FontIcon className={actionIcon} iconName="Pause" />
            <Text className={actionText} variant="medium">
              Pause
            </Text>
          </div>
        )}
        {getTime() !== 0 && getTimerState() !== timerState.PLAYING && (
          <>
            <div className={actionButton} aria-label="Resume button" onClick={handleStart}>
              <FontIcon className={actionIcon} iconName="Play" />
              <Text className={actionText} variant="medium">
                Resume
              </Text>
            </div>
            <div className={resetBtn} aria-label="Reset button" onClick={() => setIsResetDialogOpen(true)}>
              <FontIcon className={resetIcon} iconName="Refresh" />
              <Text className={actionText} variant="small">
                Reset
              </Text>
            </div>
          </>
        )}
      </div>
      <div className={saveButtonContainer}>
        <PrimaryButton text="Save" onClick={() => setDialogOpen(true)} className={saveButton} disabled={!getTime()} />
      </div>
      <ActivityDescriptionDialog
        open={dialogOpen}
        handleClose={handleCloseDialog}
        description={activityDescription}
        setDescription={setActivityDescription}
        handleSave={handleSave}
        saving={saving}
      />
      <ResetTimeDialog open={isResetDialogOpen} handleOpen={setIsResetDialogOpen} onReset={handleManualReset} />
    </div>
  );
};

export default UserTimer;
